// Pure calculation helpers for the end-of-season review. No database calls,
// so the report screen and the AI summary use exactly the same numbers.

const OUR_SCORES = { goal: 3, point: 1, twopoint: 2 }
const THEIR_SCORES = { opp_goal: 3, opp_point: 1, opp_twopoint: 2 }

export function toMinute(e) {
  if (e.minute === null || e.minute === undefined || e.minute === '') return null
  const n = Number(e.minute)
  return Number.isNaN(n) ? null : n
}

export function fixtureResult(f) {
  const us = (f.home_goals || 0) * 3 + (f.home_points || 0)
  const them = (f.away_goals || 0) * 3 + (f.away_points || 0)
  return { us, them, margin: us - them, result: us > them ? 'W' : us < them ? 'L' : 'D' }
}

export function eligibleFixtures(fixtures, { sport = null, includeFriendlies = false } = {}) {
  return fixtures.filter(f =>
    f.status === 'completed' &&
    (!sport || f.sport === sport) &&
    (includeFriendlies || (f.game_type || 'league') !== 'friendly')
  )
}

export function teamNumbersIn(fixtures) {
  return [...new Set(fixtures.map(f => Number(f.team_number || 1)))].sort((a, b) => a - b)
}

// Do the logged score events add up to the final score on the fixture?
export function reconcileFixture(fixture, events) {
  const { us, them } = fixtureResult(fixture)
  let eventsUs = 0
  let eventsThem = 0
  let unattributed = 0
  events.forEach(e => {
    if (OUR_SCORES[e.event_type]) {
      eventsUs += OUR_SCORES[e.event_type]
      if (!e.player_id) unattributed += OUR_SCORES[e.event_type]
    } else if (THEIR_SCORES[e.event_type]) {
      eventsThem += THEIR_SCORES[e.event_type]
    }
  })
  return { eventsUs, eventsThem, unattributed, matches: eventsUs === us && eventsThem === them }
}

// A "full" timeline means the match clock really ran through the whole game.
export function timelineQuality(events, halfLengthMins) {
  const matchLen = halfLengthMins * 2
  const mins = events.map(toMinute).filter(m => m !== null)
  const maxMinute = mins.length ? Math.max(...mins) : 0
  const distinct = new Set(mins).size
  return { maxMinute, full: maxMinute >= matchLen - 6 && distinct >= 8 }
}

export function scoreTimeline(events) {
  const out = []
  events.forEach((e, i) => {
    const m = toMinute(e)
    if (m === null) return
    const ours = OUR_SCORES[e.event_type]
    const theirs = THEIR_SCORES[e.event_type]
    if (!ours && !theirs) return
    out.push({ i, minute: m, side: ours ? 'us' : 'them', value: ours || theirs, source: e.notes || null, playerId: e.player_id || null })
  })
  return out.sort((a, b) => a.minute - b.minute || a.i - b.i)
}

export function scoreBetween(timeline, from, to) {
  let us = 0
  let them = 0
  timeline.forEach(s => {
    if (s.minute >= from && s.minute < to) {
      if (s.side === 'us') us += s.value
      else them += s.value
    }
  })
  return { us, them }
}

// Running score through the match, for the match-flow chart.
export function matchFlow(timeline, matchLen) {
  let us = 0
  let them = 0
  const points = [{ minute: 0, us: 0, them: 0, margin: 0, side: null, value: 0 }]
  timeline.forEach(s => {
    if (s.side === 'us') us += s.value
    else them += s.value
    points.push({ minute: Math.min(s.minute, matchLen), us, them, margin: us - them, side: s.side, value: s.value })
  })
  return points
}

// Points scored / conceded by slice of the match, across several games.
export function scoringByBand(timelines, matchLen, bandCount = 6) {
  const size = matchLen / bandCount
  const bands = Array.from({ length: bandCount }, (_, i) => ({ from: i * size, to: (i + 1) * size, us: 0, them: 0 }))
  timelines.forEach(tl => {
    tl.forEach(s => {
      const idx = Math.min(bandCount - 1, Math.floor(Math.min(s.minute, matchLen - 0.001) / size))
      if (s.side === 'us') bands[idx].us += s.value
      else bands[idx].them += s.value
    })
  })
  return bands
}

// When was each player on the pitch? Handles starters, subs coming on and off
// several times (e.g. black card cover) and red cards.
function onPitchIntervals(lineupRows, events, matchLen) {
  const byPlayer = {}
  events.forEach((e, i) => {
    if (!e.player_id) return
    if (e.event_type !== 'sub_on' && e.event_type !== 'sub_off' && e.event_type !== 'red_card') return
    const m = toMinute(e)
    if (m === null) return
    if (!byPlayer[e.player_id]) byPlayer[e.player_id] = []
    byPlayer[e.player_id].push({ i, minute: m, type: e.event_type })
  })
  const result = {}
  lineupRows.forEach(l => {
    const pid = l.player_id || l.players?.id
    if (!pid) return
    const evs = (byPlayer[pid] || []).sort((a, b) => a.minute - b.minute || a.i - b.i)
    const intervals = []
    let open = l.starting ? 0 : null
    evs.forEach(ev => {
      if (ev.type === 'sub_on') {
        if (open === null) open = ev.minute
      } else if (open !== null) {
        intervals.push([open, Math.max(open, Math.min(ev.minute, matchLen))])
        open = null
      }
    })
    if (open !== null) intervals.push([open, matchLen])
    result[pid] = intervals
  })
  return result
}

export function subSummary(events, timeline, matchLen) {
  const subOns = events.filter(e => e.event_type === 'sub_on')
  const minutes = [...new Set(subOns.map(toMinute).filter(m => m !== null))].sort((a, b) => a - b)
  if (minutes.length === 0) return { subsMade: 0, windows: [], firstSubMinute: null, beforeFirst: null, afterFirst: null }
  const first = minutes[0]
  return {
    subsMade: subOns.length,
    windows: minutes,
    firstSubMinute: first,
    beforeFirst: { ...scoreBetween(timeline, 0, first), minutes: first },
    afterFirst: { ...scoreBetween(timeline, first, Infinity), minutes: Math.max(0, matchLen - first) },
  }
}

// One row per player: games, starts, sub appearances, minutes, scoring split.
export function buildPlayerStats({ fixtures, lineups, events, halfLengthMins = 30 }) {
  const matchLen = halfLengthMins * 2
  const players = {}
  const ensure = (id, info) => {
    if (!id) return null
    if (!players[id]) {
      players[id] = {
        id, name: info?.full_name || 'Unknown', jersey: info?.jersey_number || null,
        inSquad: new Set(), started: new Set(), cameOn: new Set(), timedGames: new Set(),
        minutes: 0, scoredOn: 0, concededOn: 0,
        goals: 0, twoPointers: 0, points: 0, fromPlay: 0, fromFrees: 0, fromOther: 0,
      }
    } else if (info?.full_name && players[id].name === 'Unknown') {
      players[id].name = info.full_name
    }
    return players[id]
  }

  fixtures.forEach(f => {
    const fEvents = events.filter(e => e.fixture_id === f.id)
    const fLineups = lineups.filter(l => l.fixture_id === f.id)
    const quality = timelineQuality(fEvents, halfLengthMins)
    const timeline = scoreTimeline(fEvents)

    fLineups.forEach(l => {
      const p = ensure(l.player_id || l.players?.id, l.players)
      if (!p) return
      p.inSquad.add(f.id)
      if (l.starting) p.started.add(f.id)
    })

    fEvents.forEach(e => {
      if (!e.player_id) return
      const p = ensure(e.player_id, e.players)
      if (e.event_type === 'sub_on') p.cameOn.add(f.id)
      const value = OUR_SCORES[e.event_type]
      if (value) {
        if (e.event_type === 'goal') p.goals++
        else if (e.event_type === 'twopoint') p.twoPointers++
        else p.points++
        if (e.notes === 'from_play') p.fromPlay += value
        else if (e.notes === 'free') p.fromFrees += value
        else p.fromOther += value
      }
    })

    // Minutes and on-pitch scoring only from games where the clock really ran
    if (quality.full) {
      const intervals = onPitchIntervals(fLineups, fEvents, matchLen)
      Object.keys(intervals).forEach(pid => {
        const p = players[pid]
        if (!p) return
        p.timedGames.add(f.id)
        intervals[pid].forEach(([start, end]) => {
          p.minutes += end - start
          timeline.forEach(s => {
            if (s.minute >= start && (s.minute < end || end >= matchLen)) {
              if (s.side === 'us') p.scoredOn += s.value
              else p.concededOn += s.value
            }
          })
        })
      })
    }
  })

  return Object.values(players).map(p => {
    const played = new Set([...p.started, ...p.cameOn])
    return {
      id: p.id, name: p.name, jersey: p.jersey,
      gamesInSquad: p.inSquad.size,
      gamesPlayed: played.size,
      gamesStarted: p.started.size,
      gamesAsSub: [...p.cameOn].filter(id => !p.started.has(id)).length,
      gamesNotUsed: Math.max(0, p.inSquad.size - played.size),
      minutes: Math.round(p.minutes),
      minutesGames: p.timedGames.size,
      goals: p.goals, twoPointers: p.twoPointers, points: p.points,
      total: p.goals * 3 + p.twoPointers * 2 + p.points,
      fromPlay: p.fromPlay, fromFrees: p.fromFrees, fromOther: p.fromOther,
      scoredOn: p.scoredOn, concededOn: p.concededOn,
    }
  }).sort((a, b) => a.name.localeCompare(b.name))
}

// Positive-only: players for whom the team did notably well while they were on.
// Heavily affected by opposition and teammates, so it is a talking point only.
export function onPitchSpotlights(playerRows, baseline, matchLen, { minMinutes = 150 } = {}) {
  if (!baseline || baseline.fullGames < 3) return { defensive: [], attacking: [] }
  const eligible = playerRows.filter(p => p.minutes >= minMinutes)
  const rate = (value, p) => Math.round((value / p.minutes) * matchLen * 10) / 10
  const defensive = eligible
    .map(p => ({ id: p.id, name: p.name, minutes: p.minutes, conceded: rate(p.concededOn, p) }))
    .filter(p => p.conceded <= baseline.concededPerMatch * 0.8)
    .sort((a, b) => a.conceded - b.conceded)
    .slice(0, 4)
  const attacking = eligible
    .map(p => ({ id: p.id, name: p.name, minutes: p.minutes, scored: rate(p.scoredOn, p) }))
    .filter(p => p.scored >= baseline.scoredPerMatch * 1.2)
    .sort((a, b) => b.scored - a.scored)
    .slice(0, 4)
  return { defensive, attacking }
}

export function seasonSummary(games) {
  const s = {
    played: games.length, wins: 0, draws: 0, losses: 0,
    pointsFor: 0, pointsAgainst: 0, form: [],
    biggestWin: null, narrowestLoss: null,
    narrowLosses: 0, // lost by 3 points or fewer
    closeGames: { total: 0, W: 0, D: 0, L: 0 }, // decided by 5 points or fewer
  }
  games.forEach(g => {
    s.pointsFor += g.us
    s.pointsAgainst += g.them
    s.form.push(g.result)
    const brief = { opponent: g.opponent, date: g.date, margin: g.margin }
    if (g.result === 'W') {
      s.wins++
      if (!s.biggestWin || g.margin > s.biggestWin.margin) s.biggestWin = brief
    } else if (g.result === 'L') {
      s.losses++
      if (!s.narrowestLoss || g.margin > s.narrowestLoss.margin) s.narrowestLoss = brief
      if (g.margin >= -3) s.narrowLosses++
    } else {
      s.draws++
    }
    if (Math.abs(g.margin) <= 5) {
      s.closeGames.total++
      s.closeGames[g.result]++
    }
  })
  s.diff = s.pointsFor - s.pointsAgainst
  return s
}

const round1 = n => Math.round(n * 10) / 10

// The one function the report screen calls, once per team.
export function buildTeamReview({ teamNumber, fixtures, lineups, events, halfLengthMins = 30, includeFriendlies = false }) {
  const matchLen = halfLengthMins * 2
  const teamFixtures = eligibleFixtures(fixtures, { includeFriendlies })
    .filter(f => Number(f.team_number || 1) === Number(teamNumber))
    .sort((a, b) => new Date(a.fixture_date) - new Date(b.fixture_date))
  const ids = new Set(teamFixtures.map(f => f.id))
  const tEvents = events.filter(e => ids.has(e.fixture_id))
  const tLineups = lineups.filter(l => ids.has(l.fixture_id))

  const players = buildPlayerStats({ fixtures: teamFixtures, lineups: tLineups, events: tEvents, halfLengthMins })
  const topScorers = players
    .filter(p => p.total > 0)
    .sort((a, b) => b.total - a.total || b.goals - a.goals)
    .slice(0, 10)
  const topScorerIds = topScorers.slice(0, 3).map(p => p.id)

  const timelines = {}
  const games = teamFixtures.map(f => {
    const fEvents = tEvents.filter(e => e.fixture_id === f.id)
    const fLineups = tLineups.filter(l => l.fixture_id === f.id)
    const quality = timelineQuality(fEvents, halfLengthMins)
    const timeline = scoreTimeline(fEvents)
    const rec = reconcileFixture(f, fEvents)
    timelines[f.id] = timeline

    let atHalf = null
    if (f.ht_home_goals !== null && f.ht_home_goals !== undefined) {
      atHalf = {
        us: f.ht_home_goals * 3 + (f.ht_home_points || 0),
        them: (f.ht_away_goals || 0) * 3 + (f.ht_away_points || 0),
        exact: true,
      }
    } else if (quality.full) {
      atHalf = { ...scoreBetween(timeline, 0, halfLengthMins), exact: false }
    }

    return {
      id: f.id, date: f.fixture_date, opponent: f.opponent, gameType: f.game_type || 'league',
      ...fixtureResult(f),
      timelineFull: quality.full, maxMinute: quality.maxMinute,
      scorersReconcile: rec.matches, unattributedPoints: rec.unattributed,
      squadSize: fLineups.length,
      topScorersInSquad: topScorerIds.filter(id => fLineups.some(l => (l.player_id || l.players?.id) === id)).length,
      atHalf,
      flow: quality.full ? matchFlow(timeline, matchLen) : null,
      subs: quality.full ? subSummary(fEvents, timeline, matchLen) : null,
    }
  })

  const fullGames = games.filter(g => g.timelineFull)
  const totals = fullGames.map(g => scoreBetween(timelines[g.id], 0, Infinity))
  const baseline = {
    fullGames: fullGames.length,
    scoredPerMatch: fullGames.length ? round1(totals.reduce((n, t) => n + t.us, 0) / fullGames.length) : 0,
    concededPerMatch: fullGames.length ? round1(totals.reduce((n, t) => n + t.them, 0) / fullGames.length) : 0,
  }

  return {
    teamNumber: Number(teamNumber),
    games,
    summary: seasonSummary(games),
    bands: scoringByBand(fullGames.map(g => timelines[g.id]), matchLen),
    players,
    topScorers,
    baseline,
    spotlights: onPitchSpotlights(players, baseline, matchLen),
    dataNotes: {
      fullTimelineGames: fullGames.length,
      partialTimelineGames: games.length - fullGames.length,
      scorerMismatchGames: games.filter(g => !g.scorersReconcile).map(g => ({ opponent: g.opponent, date: g.date })),
      unattributedPoints: games.reduce((n, g) => n + g.unattributedPoints, 0),
    },
  }
}

// Pre-digested facts for the AI narrative. All arithmetic happens here so the
// model only has to interpret numbers, never calculate them.
export function buildInsightFacts(review, halfLengthMins = 30) {
  const half = halfLengthMins
  const matchLen = half * 2
  const games = review.games
  const sumBy = (arr, k) => arr.reduce((n, x) => n + (x[k] || 0), 0)
  const avg = list => (list.length ? Math.round((list.reduce((n, g) => n + g.margin, 0) / list.length) * 10) / 10 : null)

  const halfRecord = {
    ledAtHalf: { games: 0, won: 0, drawn: 0, lost: 0 },
    trailedAtHalf: { games: 0, won: 0, drawn: 0, lost: 0 },
    levelAtHalf: { games: 0, won: 0, drawn: 0, lost: 0 },
  }
  games.filter(g => g.atHalf).forEach(g => {
    const key = g.atHalf.us > g.atHalf.them ? 'ledAtHalf' : g.atHalf.us < g.atHalf.them ? 'trailedAtHalf' : 'levelAtHalf'
    const r = halfRecord[key]
    r.games++
    if (g.result === 'W') r.won++
    else if (g.result === 'D') r.drawn++
    else r.lost++
  })

  const firstHalfBands = review.bands.filter(b => b.to <= half + 0.001)
  const secondHalfBands = review.bands.filter(b => b.from >= half - 0.001)

  const lateStart = matchLen - 10
  const earlyEnd = 10
  const early = { us: 0, them: 0 }
  const late = { us: 0, them: 0 }
  games.filter(g => g.flow).forEach(g => {
    g.flow.slice(1).forEach(p => {
      const bucket = p.minute < earlyEnd ? early : p.minute >= lateStart ? late : null
      if (bucket) bucket[p.side] += p.value
    })
  })

  const topN = Math.min(3, review.topScorers.length)
  const withAll = games.filter(g => topN > 0 && g.topScorersInSquad >= topN)
  const missingOne = games.filter(g => topN > 0 && g.topScorersInSquad < topN)

  const subGames = games.filter(g => g.subs && g.subs.subsMade > 0)
  const per10 = (v, m) => (m > 0 ? Math.round((v / m) * 100) / 10 : null)
  const subTotals = subGames.reduce((t, g) => ({
    bUs: t.bUs + g.subs.beforeFirst.us, bThem: t.bThem + g.subs.beforeFirst.them, bMin: t.bMin + g.subs.beforeFirst.minutes,
    aUs: t.aUs + g.subs.afterFirst.us, aThem: t.aThem + g.subs.afterFirst.them, aMin: t.aMin + g.subs.afterFirst.minutes,
  }), { bUs: 0, bThem: 0, bMin: 0, aUs: 0, aThem: 0, aMin: 0 })

  const totalPlayerPoints = sumBy(review.players, 'total')
  const topThreePoints = review.topScorers.slice(0, 3).reduce((n, p) => n + p.total, 0)
  const playPoints = sumBy(review.players, 'fromPlay')
  const freePoints = sumBy(review.players, 'fromFrees')
  const sizes = games.map(g => g.squadSize).filter(n => n > 0)

  return {
    record: {
      played: review.summary.played, won: review.summary.wins, drawn: review.summary.draws, lost: review.summary.losses,
      pointsFor: review.summary.pointsFor, pointsAgainst: review.summary.pointsAgainst, difference: review.summary.diff,
    },
    lostByThreeOrFewer: review.summary.narrowLosses,
    decidedByFiveOrFewer: review.summary.closeGames,
    biggestWin: review.summary.biggestWin,
    narrowestDefeat: review.summary.narrowestLoss,
    halfTimeRecord: halfRecord,
    firstHalf: { scored: sumBy(firstHalfBands, 'us'), conceded: sumBy(firstHalfBands, 'them') },
    secondHalf: { scored: sumBy(secondHalfBands, 'us'), conceded: sumBy(secondHalfBands, 'them') },
    firstTenMinutes: { scored: early.us, conceded: early.them },
    lastTenMinutes: { scored: late.us, conceded: late.them },
    gamesWithFullTimeline: review.dataNotes.fullTimelineGames,
    availability: {
      topScorersConsidered: topN,
      gamesWithAllOfThemInSquad: withAll.length, averageMarginWithAll: avg(withAll),
      gamesMissingAtLeastOne: missingOne.length, averageMarginMissingOne: avg(missingOne),
    },
    substitutions: {
      gamesWithSubs: subGames.length,
      beforeFirstSubPerTenMinutes: { scored: per10(subTotals.bUs, subTotals.bMin), conceded: per10(subTotals.bThem, subTotals.bMin) },
      afterFirstSubPerTenMinutes: { scored: per10(subTotals.aUs, subTotals.aMin), conceded: per10(subTotals.aThem, subTotals.aMin) },
    },
    scoring: {
      differentScorers: review.players.filter(p => p.total > 0).length,
      topThreeSharePercent: totalPlayerPoints ? Math.round((topThreePoints / totalPlayerPoints) * 100) : null,
      fromPlayPercent: playPoints + freePoints ? Math.round((playPoints / (playPoints + freePoints)) * 100) : null,
    },
    squad: {
      playersUsed: review.players.filter(p => p.gamesPlayed > 0).length,
      matchSquadSizeRange: sizes.length ? [Math.min(...sizes), Math.max(...sizes)] : null,
    },
    // Names are only ever sent for positive recognition
    topScorers: review.topScorers.slice(0, 5).map(p => ({ name: p.name, totalPoints: p.total, goals: p.goals })),
    spotlights: {
      defensive: review.spotlights.defensive.map(p => ({ name: p.name, concededPerMatchWhileOn: p.conceded })),
      attacking: review.spotlights.attacking.map(p => ({ name: p.name, scoredPerMatchWhileOn: p.scored })),
      teamAverages: { scoredPerMatch: review.baseline.scoredPerMatch, concededPerMatch: review.baseline.concededPerMatch },
    },
    games: games.map(g => ({
      opponent: g.opponent, date: g.date, result: g.result, us: g.us, them: g.them, margin: g.margin,
      half: g.atHalf ? { us: g.atHalf.us, them: g.atHalf.them, estimated: !g.atHalf.exact } : null,
      fullTimeline: g.timelineFull,
      matchSquadSize: g.squadSize,
      subsMade: g.subs ? g.subs.subsMade : null,
    })),
    dataNotes: review.dataNotes,
  }
}